import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

/*
 * Admin > Mercadeo: fotos, videos y enlaces de redes para la pagina publica /mercadeo.
 * Cada elemento tiene categoria, prioridad (1 Destacado ... 5 Minima) y fecha opcional
 * de vencimiento. En el sitio se ordena por prioridad y, dentro de cada una, con las flechas.
 */

const API = 'https://ticobaterias.com/api';
const SITIO = 'https://ticobaterias.com';
const EXT_IMAGEN = ['jpg', 'jpeg', 'png', 'webp', 'gif'];
const EXT_VIDEO = ['mp4', 'webm', 'mov'];
const MAX_IMAGEN_MB = 15;
const MAX_VIDEO_MB = 100;

const PRIORIDADES = [
  { valor: 1, texto: '⭐ 1 · Destacado', corto: '⭐ Destacado', clase: 'bg-yellow-400 text-gray-900' },
  { valor: 2, texto: '2 · Alta', corto: 'Alta', clase: 'bg-azul text-white' },
  { valor: 3, texto: '3 · Normal', corto: 'Normal', clase: 'bg-gray-200 text-gray-700' },
  { valor: 4, texto: '4 · Baja', corto: 'Baja', clase: 'bg-gray-100 text-gray-500' },
  { valor: 5, texto: '5 · Mínima', corto: 'Mínima', clase: 'bg-gray-100 text-gray-400' },
];
const EMOJIS = ['🏷️', '🔧', '🎉', '💡', '🔋', '⚡', '🚗', '🏍️', '☀️', '🎁', '🛠️', '📣'];
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'set', 'oct', 'nov', 'dic'];

type Elemento = {
  id: number;
  titulo: string;
  descripcion: string;
  tipo: 'imagen' | 'video' | 'enlace';
  archivo_url: string | null;
  enlace_url: string | null;
  tamano_bytes: number;
  visible: boolean;
  categoria_id: number | null;
  prioridad: number;
  visible_hasta: string | null;
  vencido: boolean;
};

type Categoria = { id: number; nombre: string; icono: string };

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const mb = (bytes: number) => (bytes / 1024 / 1024).toFixed(1).replace('.0', '') + ' MB';
const extension = (nombre: string) => (nombre.includes('.') ? nombre.split('.').pop()!.toLowerCase() : '');
const fechaCorta = (iso: string) => { const [, m, d] = iso.split('-'); return `${parseInt(d, 10)} ${MESES[parseInt(m, 10) - 1]}`; };
const prioridadDe = (p: number) => PRIORIDADES.find(x => x.valor === p) || PRIORIDADES[2];

const red = (url: string) => {
  try {
    const h = new URL(url).hostname;
    if (/youtu/.test(h)) return '▶️ YouTube';
    if (/tiktok/.test(h)) return '🎵 TikTok';
    if (/instagram/.test(h)) return '📸 Instagram';
    if (/facebook|fb\.watch/.test(h)) return '📘 Facebook';
    return '🔗 ' + h.replace(/^www\./, '');
  } catch { return '🔗 Enlace'; }
};

const errorDe = (e: any, otro: string) => e?.response?.data?.detail || (e?.response?.status === 413 ? 'El archivo es demasiado grande.' : otro);

const campo = 'w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-azul bg-white';

export default function Mercadeo() {
  const [lista, setLista] = useState<Elemento[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [modo, setModo] = useState<'archivo' | 'enlace'>('archivo');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [url, setUrl] = useState('');
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [categoriaId, setCategoriaId] = useState('');
  const [prioridad, setPrioridad] = useState(3);
  const [vence, setVence] = useState('');
  const [progreso, setProgreso] = useState<number | null>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [toast, setToast] = useState('');
  const [filtro, setFiltro] = useState('todas');
  const [editando, setEditando] = useState<Elemento | null>(null);
  const [confirmar, setConfirmar] = useState<Elemento | null>(null);
  const [catEditando, setCatEditando] = useState<Categoria | null>(null);
  const [catBorrar, setCatBorrar] = useState<Categoria | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const avisar = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const cargar = () =>
    axios.get(`${API}/mercadeo/admin`, { headers: headers() })
      .then(r => setLista(r.data))
      .catch(() => avisar('❌ No se pudo cargar la lista'));

  const cargarCategorias = () =>
    axios.get(`${API}/mercadeo/categorias`)
      .then(r => setCategorias(r.data))
      .catch(() => avisar('❌ No se pudieron cargar las categorías'));

  useEffect(() => { cargar(); cargarCategorias(); }, []);

  const nombreCategoria = (id: number | null) => {
    const c = categorias.find(x => x.id === id);
    return c ? `${c.icono} ${c.nombre}` : 'Sin categoría';
  };

  // ---------- agregar ----------

  const problemaArchivo = (f: File): string => {
    const ext = extension(f.name);
    if (EXT_IMAGEN.includes(ext)) return f.size > MAX_IMAGEN_MB * 1048576 ? `La imagen pesa ${mb(f.size)}; el máximo es ${MAX_IMAGEN_MB} MB.` : '';
    if (EXT_VIDEO.includes(ext)) return f.size > MAX_VIDEO_MB * 1048576 ? `El video pesa ${mb(f.size)}; el máximo es ${MAX_VIDEO_MB} MB. Para videos largos súbalo a YouTube, TikTok o Facebook y pegue el enlace.` : '';
    return 'Formato no permitido. Imágenes: JPG, PNG, WEBP, GIF. Videos: MP4, WEBM, MOV.';
  };

  const elegir = (f: File | undefined | null) => {
    if (!f) return;
    const p = problemaArchivo(f);
    if (p) { avisar('⚠️ ' + p); return; }
    setArchivo(f);
  };

  const limpiar = () => {
    setArchivo(null); setUrl(''); setTitulo(''); setDescripcion(''); setProgreso(null);
    setPrioridad(3); setVence('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const agregar = async () => {
    try {
      if (modo === 'archivo') {
        if (!archivo) return;
        const fd = new FormData();
        fd.append('file', archivo);
        fd.append('titulo', titulo);
        fd.append('descripcion', descripcion);
        fd.append('categoria_id', categoriaId);
        fd.append('prioridad', String(prioridad));
        fd.append('visible_hasta', vence);
        setProgreso(0);
        await axios.post(`${API}/mercadeo/archivo`, fd, {
          headers: headers(),
          onUploadProgress: ev => { if (ev.total) setProgreso(Math.round((ev.loaded / ev.total) * 100)); },
        });
      } else {
        if (!url.trim()) return;
        setProgreso(100);
        await axios.post(`${API}/mercadeo/enlace`, {
          url: url.trim(), titulo, descripcion,
          categoria_id: categoriaId || null, prioridad, visible_hasta: vence || null,
        }, { headers: headers() });
      }
      limpiar();
      await cargar();
      avisar('✅ Publicado en la página Mercadeo');
    } catch (e) {
      setProgreso(null);
      avisar('❌ ' + errorDe(e, 'No se pudo subir'));
    }
  };

  // ---------- elementos ----------

  const cambiar = async (id: number, datos: any) => {
    try {
      await axios.put(`${API}/mercadeo/${id}`, datos, { headers: headers() });
      await cargar();
      return true;
    } catch (e) { avisar('❌ ' + errorDe(e, 'No se pudo guardar')); return false; }
  };

  const mostrados = lista.filter(e =>
    filtro === 'todas' ? true
      : filtro === 'sin' ? (e.categoria_id === null || !categorias.some(c => c.id === e.categoria_id))
      : String(e.categoria_id) === filtro);

  // Las flechas mueven solo entre vecinos con la misma prioridad
  const vecino = (e: Elemento, paso: number) => {
    const i = mostrados.findIndex(x => x.id === e.id);
    const v = mostrados[i + paso];
    return v && v.prioridad === e.prioridad ? v : null;
  };

  const mover = async (e: Elemento, paso: number) => {
    const v = vecino(e, paso);
    if (!v) return;
    const nueva = lista.slice();
    const a = nueva.findIndex(x => x.id === e.id);
    const b = nueva.findIndex(x => x.id === v.id);
    nueva[a] = v; nueva[b] = e;
    setLista(nueva);
    try {
      await axios.put(`${API}/mercadeo/orden`, { ids: nueva.map(x => x.id) }, { headers: headers() });
    } catch { avisar('❌ No se pudo guardar el orden'); cargar(); }
  };

  const eliminar = async (e: Elemento) => {
    try {
      await axios.delete(`${API}/mercadeo/${e.id}`, { headers: headers() });
      setConfirmar(null);
      await cargar();
      avisar('🗑️ Eliminado');
    } catch (err) { avisar('❌ ' + errorDe(err, 'No se pudo eliminar')); }
  };

  // ---------- categorias ----------

  const guardarCategoria = async (c: Categoria) => {
    if (!c.nombre.trim()) { avisar('⚠️ Escriba el nombre de la categoría'); return; }
    try {
      if (c.id) await axios.put(`${API}/mercadeo/categorias/${c.id}`, { nombre: c.nombre, icono: c.icono }, { headers: headers() });
      else await axios.post(`${API}/mercadeo/categorias`, { nombre: c.nombre, icono: c.icono }, { headers: headers() });
      setCatEditando(null);
      await cargarCategorias();
      avisar(c.id ? '✅ Categoría actualizada' : '✅ Categoría creada');
    } catch (e) { avisar('❌ ' + errorDe(e, 'No se pudo guardar la categoría')); }
  };

  const moverCategoria = async (indice: number, paso: number) => {
    const destino = indice + paso;
    if (destino < 0 || destino >= categorias.length) return;
    const nueva = categorias.slice();
    const [c] = nueva.splice(indice, 1);
    nueva.splice(destino, 0, c);
    setCategorias(nueva);
    try {
      await axios.put(`${API}/mercadeo/categorias/orden`, { ids: nueva.map(x => x.id) }, { headers: headers() });
    } catch { avisar('❌ No se pudo guardar el orden'); cargarCategorias(); }
  };

  const borrarCategoria = async (c: Categoria) => {
    try {
      await axios.delete(`${API}/mercadeo/categorias/${c.id}`, { headers: headers() });
      setCatBorrar(null);
      if (filtro === String(c.id)) setFiltro('todas');
      if (categoriaId === String(c.id)) setCategoriaId('');
      await cargarCategorias();
      await cargar();
      avisar('🗑️ Categoría eliminada');
    } catch (e) { avisar('❌ ' + errorDe(e, 'No se pudo eliminar')); }
  };

  const subiendo = progreso !== null;
  const listo = modo === 'archivo' ? !!archivo : url.trim().length > 0;
  const espacio = lista.reduce((t, e) => t + (e.tamano_bytes || 0), 0);
  const cuantos = (id: number) => lista.filter(e => e.categoria_id === id).length;
  const sinCategoria = lista.filter(e => e.categoria_id === null || !categorias.some(c => c.id === e.categoria_id)).length;

  const vista = (e: Elemento) => {
    if (e.tipo === 'imagen' && e.archivo_url) return <img src={SITIO + e.archivo_url} alt={e.titulo} className="w-full h-full object-cover" />;
    if (e.tipo === 'video' && e.archivo_url) return <video src={`${SITIO}${e.archivo_url}#t=0.1`} preload="metadata" muted playsInline controls className="w-full h-full object-cover bg-black" />;
    return (
      <a href={e.enlace_url || '#'} target="_blank" rel="noreferrer" className="w-full h-full bg-azul flex flex-col items-center justify-center text-white p-3 text-center hover:bg-azul-dark">
        <div className="text-2xl font-bold">{red(e.enlace_url || '')}</div>
        <div className="text-xs text-white/60 mt-2 break-all line-clamp-2">{e.enlace_url}</div>
      </a>
    );
  };

  const selectorCategoria = (valor: string, cambiarA: (v: string) => void, deshabilitado = false) => (
    <select value={valor} onChange={ev => cambiarA(ev.target.value)} disabled={deshabilitado} className={campo}>
      <option value="">Sin categoría</option>
      {categorias.map(c => <option key={c.id} value={String(c.id)}>{c.icono} {c.nombre}</option>)}
    </select>
  );

  const selectorPrioridad = (valor: number, cambiarA: (v: number) => void, deshabilitado = false) => (
    <select value={valor} onChange={ev => cambiarA(Number(ev.target.value))} disabled={deshabilitado} className={campo}>
      {PRIORIDADES.map(p => <option key={p.valor} value={p.valor}>{p.texto}</option>)}
    </select>
  );

  const chip = (activo: boolean) =>
    `px-4 py-1.5 rounded-full text-sm font-medium transition-all border ${activo ? 'bg-azul text-white border-azul' : 'bg-white text-gray-600 border-gray-200 hover:border-azul hover:text-azul'}`;

  return (
    <div>
      {toast && (
        <div className="fixed top-6 right-6 z-[60] bg-white border border-gray-200 shadow-xl rounded-xl px-5 py-3 text-sm font-semibold text-gray-700 max-w-md">
          {toast}
        </div>
      )}

      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-azul">📣 Mercadeo</h1>
          <p className="text-gray-500 text-sm mt-1">Fotos y videos que aparecen en la página Mercadeo del sitio</p>
        </div>
        <a href={`${SITIO}/mercadeo`} target="_blank" rel="noreferrer"
          className="text-sm font-semibold text-azul border border-azul/30 hover:bg-azul hover:text-white px-4 py-2 rounded-xl transition-all">
          👁️ Ver página
        </a>
      </div>

      {/* Categorias */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
        <div className="flex justify-between items-center mb-3 gap-3 flex-wrap">
          <div>
            <h2 className="font-bold text-azul">🗂️ Categorías</h2>
            <p className="text-xs text-gray-400">En el sitio aparecen como pestañas, en este orden. Las que no tienen nada publicado no se muestran.</p>
          </div>
          <button type="button" onClick={() => setCatEditando({ id: 0, nombre: '', icono: '🏷️' })}
            className="bg-azul hover:bg-azul-dark text-white px-4 py-2 rounded-xl text-sm font-semibold">+ Nueva categoría</button>
        </div>
        {categorias.length === 0 ? (
          <div className="text-sm text-gray-400">Todavía no hay categorías.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {categorias.map((c, i) => (
              <div key={c.id} className="flex items-center gap-1 border border-gray-200 rounded-xl pl-3 pr-1 py-1">
                <span className="text-sm font-semibold text-gray-700 mr-1">{c.icono} {c.nombre}</span>
                <span className="text-xs text-gray-400 mr-1">({cuantos(c.id)})</span>
                <button type="button" title="Mover a la izquierda" disabled={i === 0} onClick={() => moverCategoria(i, -1)}
                  className="w-7 h-7 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-25 text-xs">◀</button>
                <button type="button" title="Mover a la derecha" disabled={i === categorias.length - 1} onClick={() => moverCategoria(i, 1)}
                  className="w-7 h-7 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-25 text-xs">▶</button>
                <button type="button" title="Editar" onClick={() => setCatEditando({ ...c })} className="w-7 h-7 rounded-lg hover:bg-gray-100 text-sm">✏️</button>
                <button type="button" title="Eliminar" onClick={() => setCatBorrar(c)} className="w-7 h-7 rounded-lg hover:bg-red-50 text-sm">🗑️</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Agregar */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8">
        <div className="flex gap-2 mb-5 flex-wrap">
          {([['archivo', '📁 Subir foto o video'], ['enlace', '🔗 Pegar enlace de red social']] as const).map(([valor, texto]) => (
            <button key={valor} type="button" disabled={subiendo} onClick={() => setModo(valor)}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${modo === valor ? 'bg-azul text-white border-azul' : 'bg-white text-gray-600 border-gray-200 hover:border-azul'}`}>
              {texto}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            {modo === 'archivo' ? (
              <label
                onDragOver={e => { e.preventDefault(); setArrastrando(true); }}
                onDragLeave={() => setArrastrando(false)}
                onDrop={e => { e.preventDefault(); setArrastrando(false); elegir(e.dataTransfer.files[0]); }}
                className={`cursor-pointer flex flex-col items-center justify-center h-full min-h-[11rem] border-2 border-dashed rounded-xl p-6 text-center transition-all ${arrastrando ? 'border-azul bg-azul/5' : archivo ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-azul'}`}>
                {archivo ? (
                  <>
                    <div className="text-3xl mb-2">{EXT_VIDEO.includes(extension(archivo.name)) ? '🎬' : '🖼️'}</div>
                    <div className="text-sm font-semibold text-gray-700 break-all">{archivo.name}</div>
                    <div className="text-xs text-gray-400 mt-1">{mb(archivo.size)} · clic para cambiar</div>
                  </>
                ) : (
                  <>
                    <div className="text-3xl mb-2">⬆️</div>
                    <div className="text-sm font-medium text-gray-600">Arrastre aquí o haga clic para elegir</div>
                    <div className="text-xs text-gray-400 mt-1">Fotos JPG, PNG, WEBP, GIF (hasta {MAX_IMAGEN_MB} MB)</div>
                    <div className="text-xs text-gray-400">Videos MP4, WEBM, MOV (hasta {MAX_VIDEO_MB} MB)</div>
                  </>
                )}
                <input ref={inputRef} type="file" className="hidden" disabled={subiendo}
                  accept=".jpg,.jpeg,.png,.webp,.gif,.mp4,.webm,.mov,image/*,video/mp4,video/webm,video/quicktime"
                  onChange={e => elegir(e.target.files && e.target.files[0])} />
              </label>
            ) : (
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Enlace *</label>
                <input value={url} onChange={e => setUrl(e.target.value)} disabled={subiendo}
                  placeholder="https://www.tiktok.com/@ticobaterias/video/..." className={campo} />
                <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                  Abra la publicación en YouTube, TikTok, Facebook o Instagram, toque <b>Compartir → Copiar enlace</b> y péguelo aquí.
                  El video se verá dentro de la página sin gastar espacio del sitio.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Título (opcional)</label>
              <input value={titulo} onChange={e => setTitulo(e.target.value)} maxLength={200} disabled={subiendo}
                placeholder="Ej.: Promo de octubre" className={campo} />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Descripción (opcional)</label>
              <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} rows={2} disabled={subiendo}
                placeholder="Detalle de la promoción..." className={campo + ' resize-none'} />
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-4">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Categoría</label>
            {selectorCategoria(categoriaId, setCategoriaId, subiendo)}
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Prioridad</label>
            {selectorPrioridad(prioridad, setPrioridad, subiendo)}
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Visible hasta (opcional)</label>
            <div className="flex gap-2">
              <input type="date" value={vence} onChange={e => setVence(e.target.value)} disabled={subiendo} className={campo} />
              {vence && <button type="button" onClick={() => setVence('')} title="Quitar fecha" className="px-3 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50">✕</button>}
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-2">⭐ Destacado sale arriba y en grande. Con fecha, se oculta sola del sitio después de ese día.</p>

        {subiendo && modo === 'archivo' && (
          <div className="mt-5">
            <div className="flex justify-between text-xs font-semibold text-gray-500 mb-1">
              <span>{progreso! < 100 ? 'Subiendo…' : 'Procesando…'}</span><span>{progreso}%</span>
            </div>
            <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-azul transition-all" style={{ width: `${progreso}%` }} />
            </div>
            <p className="text-xs text-gray-400 mt-1">No cierre esta página hasta que termine.</p>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-5">
          {listo && !subiendo && (
            <button type="button" onClick={limpiar} className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">
              Cancelar
            </button>
          )}
          <button type="button" onClick={agregar} disabled={!listo || subiendo}
            className="px-6 py-2.5 rounded-xl bg-rojo hover:bg-rojo-dark text-white text-sm font-semibold transition-all disabled:opacity-40">
            {subiendo ? 'Subiendo…' : '📣 Publicar'}
          </button>
        </div>
      </div>

      {/* Lista */}
      <div className="flex justify-between items-baseline mb-3 flex-wrap gap-2">
        <h2 className="font-bold text-azul">Publicado ({lista.length})</h2>
        {espacio > 0 && <span className="text-xs text-gray-400">Archivos de Mercadeo: {mb(espacio)}</span>}
      </div>

      {lista.length > 0 && (
        <div className="flex gap-2 flex-wrap mb-5">
          <button type="button" className={chip(filtro === 'todas')} onClick={() => setFiltro('todas')}>Todas ({lista.length})</button>
          {categorias.map(c => (
            <button key={c.id} type="button" className={chip(filtro === String(c.id))} onClick={() => setFiltro(String(c.id))}>
              {c.icono} {c.nombre} ({cuantos(c.id)})
            </button>
          ))}
          {sinCategoria > 0 && <button type="button" className={chip(filtro === 'sin')} onClick={() => setFiltro('sin')}>Sin categoría ({sinCategoria})</button>}
        </div>
      )}

      {mostrados.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 text-gray-400">
          <div className="text-4xl mb-3">📣</div>
          <div className="font-medium">{lista.length === 0 ? 'Todavía no hay nada publicado' : 'No hay nada en esta categoría'}</div>
          {lista.length === 0 && <div className="text-sm mt-1">Suba la primera foto o video con el recuadro de arriba</div>}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {mostrados.map(e => {
            const p = prioridadDe(e.prioridad);
            const apagado = !e.visible || e.vencido;
            return (
              <div key={e.id} className={`bg-white rounded-2xl border shadow-sm overflow-hidden flex flex-col ${apagado ? 'border-dashed border-gray-300' : e.prioridad === 1 ? 'border-yellow-400 ring-1 ring-yellow-300' : 'border-gray-100'}`}>
                <div className={`relative h-44 bg-gray-100 ${apagado ? 'opacity-50' : ''}`}>
                  {vista(e)}
                  <span className="absolute top-2 left-2 bg-black/60 text-white text-[11px] font-bold px-2 py-0.5 rounded-full">
                    {e.tipo === 'imagen' ? 'FOTO' : e.tipo === 'video' ? 'VIDEO' : 'ENLACE'}
                    {e.tamano_bytes > 0 ? ` · ${mb(e.tamano_bytes)}` : ''}
                  </span>
                  {!e.visible && <span className="absolute top-2 right-2 bg-gray-800 text-white text-[11px] font-bold px-2 py-0.5 rounded-full">OCULTO</span>}
                  {e.visible && e.vencido && <span className="absolute top-2 right-2 bg-rojo text-white text-[11px] font-bold px-2 py-0.5 rounded-full">VENCIDO</span>}
                </div>
                <div className="p-3 flex-1">
                  <div className="flex flex-wrap gap-1 mb-2">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${p.clase}`}>{p.corto}</span>
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-azul/10 text-azul">{nombreCategoria(e.categoria_id)}</span>
                    {e.visible_hasta && (
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${e.vencido ? 'bg-red-100 text-rojo' : 'bg-orange-50 text-orange-600'}`}>
                        ⏳ hasta {fechaCorta(e.visible_hasta)}
                      </span>
                    )}
                  </div>
                  <div className="font-semibold text-gray-800 text-sm truncate">{e.titulo || <span className="text-gray-400 font-normal">Sin título</span>}</div>
                  {e.descripcion && <div className="text-xs text-gray-400 mt-0.5 line-clamp-2">{e.descripcion}</div>}
                </div>
                <div className="px-3 pb-3 flex items-center gap-1.5">
                  <button type="button" title="Mover antes (misma prioridad)" disabled={!vecino(e, -1)} onClick={() => mover(e, -1)}
                    className="w-8 h-8 rounded-lg border border-gray-200 text-gray-600 hover:border-azul disabled:opacity-30">◀</button>
                  <button type="button" title="Mover después (misma prioridad)" disabled={!vecino(e, 1)} onClick={() => mover(e, 1)}
                    className="w-8 h-8 rounded-lg border border-gray-200 text-gray-600 hover:border-azul disabled:opacity-30">▶</button>
                  <button type="button" onClick={() => cambiar(e.id, { visible: !e.visible })}
                    className={`flex-1 h-8 rounded-lg text-xs font-semibold ${e.visible ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    {e.visible ? '👁️ Visible' : '🙈 Oculto'}
                  </button>
                  <button type="button" title="Editar" onClick={() => setEditando({ ...e })}
                    className="w-8 h-8 rounded-lg border border-gray-200 hover:border-azul">✏️</button>
                  <button type="button" title="Eliminar" onClick={() => setConfirmar(e)}
                    className="w-8 h-8 rounded-lg border border-gray-200 hover:border-rojo">🗑️</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Editar elemento */}
      {editando && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100"><h2 className="text-lg font-bold text-azul">Editar</h2></div>
            <div className="p-6 space-y-4">
              {editando.tipo === 'enlace' && (
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Enlace</label>
                  <input value={editando.enlace_url || ''} onChange={ev => setEditando({ ...editando, enlace_url: ev.target.value })} className={campo} />
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Título</label>
                <input value={editando.titulo} maxLength={200} onChange={ev => setEditando({ ...editando, titulo: ev.target.value })} className={campo} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Descripción</label>
                <textarea value={editando.descripcion} rows={3} onChange={ev => setEditando({ ...editando, descripcion: ev.target.value })} className={campo + ' resize-none'} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Categoría</label>
                  {selectorCategoria(editando.categoria_id ? String(editando.categoria_id) : '', v => setEditando({ ...editando, categoria_id: v ? Number(v) : null }))}
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Prioridad</label>
                  {selectorPrioridad(editando.prioridad, v => setEditando({ ...editando, prioridad: v }))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Visible hasta (opcional)</label>
                <div className="flex gap-2">
                  <input type="date" value={editando.visible_hasta || ''} onChange={ev => setEditando({ ...editando, visible_hasta: ev.target.value || null })} className={campo} />
                  {editando.visible_hasta && <button type="button" onClick={() => setEditando({ ...editando, visible_hasta: null })} title="Quitar fecha" className="px-3 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50">✕</button>}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex gap-3 justify-end">
              <button type="button" onClick={() => setEditando(null)} className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button type="button"
                onClick={async () => {
                  const datos: any = {
                    titulo: editando.titulo, descripcion: editando.descripcion,
                    categoria_id: editando.categoria_id, prioridad: editando.prioridad, visible_hasta: editando.visible_hasta,
                  };
                  if (editando.tipo === 'enlace') datos.enlace_url = editando.enlace_url;
                  if (await cambiar(editando.id, datos)) { setEditando(null); avisar('✅ Cambios guardados'); }
                }}
                className="px-5 py-2.5 rounded-xl bg-rojo hover:bg-rojo-dark text-white text-sm font-semibold">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Crear / editar categoria */}
      {catEditando && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-100"><h2 className="text-lg font-bold text-azul">{catEditando.id ? 'Editar categoría' : 'Nueva categoría'}</h2></div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Nombre *</label>
                <input value={catEditando.nombre} maxLength={100} autoFocus placeholder="Ej.: Promociones"
                  onChange={ev => setCatEditando({ ...catEditando, nombre: ev.target.value })} className={campo} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Ícono</label>
                <div className="flex flex-wrap gap-1.5">
                  {EMOJIS.map(em => (
                    <button key={em} type="button" onClick={() => setCatEditando({ ...catEditando, icono: em })}
                      className={`w-10 h-10 rounded-xl text-xl border ${catEditando.icono === em ? 'border-azul bg-azul/10' : 'border-gray-200 hover:border-azul'}`}>{em}</button>
                  ))}
                  <input value={catEditando.icono} maxLength={8} title="O escriba otro emoji"
                    onChange={ev => setCatEditando({ ...catEditando, icono: ev.target.value })}
                    className="w-14 h-10 border border-gray-200 rounded-xl text-center text-xl focus:outline-none focus:border-azul" />
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex gap-3 justify-end">
              <button type="button" onClick={() => setCatEditando(null)} className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button type="button" onClick={() => guardarCategoria(catEditando)} disabled={!catEditando.nombre.trim()}
                className="px-5 py-2.5 rounded-xl bg-rojo hover:bg-rojo-dark text-white text-sm font-semibold disabled:opacity-40">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmar eliminar categoria */}
      {catBorrar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="text-4xl text-center mb-3">🗂️</div>
            <h3 className="text-lg font-bold text-gray-800 text-center mb-2">¿Eliminar “{catBorrar.icono} {catBorrar.nombre}”?</h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              {cuantos(catBorrar.id) > 0
                ? `Sus ${cuantos(catBorrar.id)} fotos y videos no se borran: quedan “Sin categoría”.`
                : 'No tiene fotos ni videos.'}
            </p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setCatBorrar(null)} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button type="button" onClick={() => borrarCategoria(catBorrar)} className="flex-1 px-4 py-2.5 rounded-xl bg-rojo hover:bg-rojo-dark text-white text-sm font-semibold">Sí, eliminar</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmar eliminar elemento */}
      {confirmar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="text-4xl text-center mb-3">🗑️</div>
            <h3 className="text-lg font-bold text-gray-800 text-center mb-2">¿Eliminar {confirmar.tipo === 'enlace' ? 'este enlace' : confirmar.tipo === 'video' ? 'este video' : 'esta foto'}?</h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              {confirmar.tipo === 'enlace' ? 'Se quita de la página. La publicación en la red social no se toca.' : 'Se borra el archivo del servidor y no se puede deshacer. Si solo quiere quitarlo un tiempo, use “Oculto”.'}
            </p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setConfirmar(null)} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button type="button" onClick={() => eliminar(confirmar)} className="flex-1 px-4 py-2.5 rounded-xl bg-rojo hover:bg-rojo-dark text-white text-sm font-semibold">Sí, eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
